import { alpha, SxProps, Theme } from '@mui/material/styles';

/** Verde e amarelo oficiais da bandeira do Brasil */
export const COPA_VERDE_BANDEIRA = '#013f1e';
/** Tom mais claro para cards dentro do sidebar (avatar, rodapé) */
export const COPA_VERDE_SIDEBAR_CARD = '#0a7040';
export const COPA_AMARELO_BANDEIRA = '#FFDF00';

/** Estilos do paper do drawer Copa — verde sólido bandeira (sem herdar paper branco do MUI) */
export const copaDrawerPaperSx = {
  bgcolor: `${COPA_VERDE_BANDEIRA} !important`,
  backgroundColor: `${COPA_VERDE_BANDEIRA} !important`,
  background: `${COPA_VERDE_BANDEIRA} !important`,
  borderRight: `1px solid ${alpha(COPA_AMARELO_BANDEIRA, 0.35)}`,
  boxShadow: `4px 0 24px ${alpha(COPA_VERDE_BANDEIRA, 0.65)}`,
  color: '#ffffff !important',
  overflowX: 'hidden',
  overflowY: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -40,
    right: -40,
    width: 140,
    height: 140,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${alpha(COPA_AMARELO_BANDEIRA, 0.22)} 0%, transparent 70%)`,
    pointerEvents: 'none',
    zIndex: 0,
  },
};

export function copaMenuItemSx(selected: boolean, level: number): SxProps<Theme> {
  const base = {
    borderRadius: 2,
    mx: 0.75,
    transition: 'background-color 0.2s, box-shadow 0.2s, color 0.2s',
    ...(level > 0 &&
      !selected && {
      ml: 2.5,
      mr: 1,
      minHeight: 36,
      borderLeft: '2px solid rgba(255,255,255,0.25)',
      pl: 1.5,
    }),
  };

  if (selected) {
    const selectedStyles = {
      bgcolor: `${COPA_AMARELO_BANDEIRA} !important`,
      color: '#1a3328',
      fontWeight: 600,
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)',
      '&:hover': { bgcolor: '#f0c200 !important' },
      '& .MuiListItemIcon-root': { color: '#1a3328 !important' },
      '& .MuiListItemIcon-root svg': { color: '#1a3328 !important' },
      '& .MuiTypography-root': { fontWeight: 600, color: '#1a3328 !important' },
      '& .MuiSvgIcon-root': { color: '#1a3328 !important' },
    };
    return {
      ...base,
      ...selectedStyles,
      '&.Mui-selected': selectedStyles,
    };
  }

  return {
    ...base,
    color: 'rgba(255,255,255,0.9)',
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.1)',
    },
    '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.9) !important' },
    '& .MuiListItemIcon-root svg': { color: 'rgba(255,255,255,0.9) !important' },
    '& .MuiTypography-root': { color: 'rgba(255,255,255,0.9)' },
    '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.9) !important' },
  };
}

export const copaSidebarUserBoxSx: SxProps<Theme> = {
  mx: 1,
  mt: 0.5,
  mb: 1,
  p: 1.25,
  borderRadius: 2,
  bgcolor: COPA_VERDE_SIDEBAR_CARD,
  border: `1px solid ${alpha(COPA_AMARELO_BANDEIRA, 0.55)}`,
  boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.08)}`,
};

export const copaSidebarBrandSx = (expanded: boolean): SxProps<Theme> => ({
  px: expanded ? 2 : 1,
  py: expanded ? 1.5 : 1.25,
  display: 'flex',
  alignItems: 'center',
  justifyContent: expanded ? 'flex-start' : 'center',
  gap: 1.25,
  borderBottom: '1px solid rgba(255, 204, 0, 0.18)',
  minHeight: expanded ? 64 : 52,
  position: 'relative',
  zIndex: 1,
});

export const copaSidebarTrophyBoxSx: SxProps<Theme> = {
  width: 40,
  height: 40,
  borderRadius: 1.5,
  bgcolor: COPA_AMARELO_BANDEIRA,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
  '& svg': {
    color: '#1a3328',
    fontSize: '1.35rem',
  },
};

export const copaSidebarBrandTitleSx: SxProps<Theme> = {
  color: COPA_AMARELO_BANDEIRA,
  fontWeight: 800,
  fontSize: '0.68rem',
  letterSpacing: 0.8,
  lineHeight: 1.25,
  textTransform: 'uppercase',
};

export const copaSidebarBrandSubtitleSx: SxProps<Theme> = {
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '0.85rem',
  lineHeight: 1.25,
  mt: 0.15,
};

export const copaSidebarDividerSx: SxProps<Theme> = {
  borderColor: 'rgba(255, 255, 255, 0.1)',
  mx: 1.5,
};

export const copaSidebarListSx: SxProps<Theme> = {
  flexGrow: 1,
  px: 0.5,
  py: 1,
  pb: 14,
  overflowY: 'auto',
  minHeight: 0,
  position: 'relative',
  zIndex: 1,
};

export const copaSidebarFooterSx: SxProps<Theme> = {
  position: 'relative',
  zIndex: 1,
  py: 2,
  px: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0.5,
  borderTop: `1px solid ${alpha(COPA_AMARELO_BANDEIRA, 0.35)}`,
  bgcolor: COPA_VERDE_SIDEBAR_CARD,
};

/** Imagem decorativa da bola — fundo inferior do sidebar */
export const copaSidebarBallBgSx = (expanded: boolean): SxProps<Theme> => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: expanded ? 'auto' : 88,
  maxHeight: expanded ? 200 : 100,
  objectFit: 'cover',
  objectPosition: 'bottom center',
  pointerEvents: 'none',
  zIndex: 0,
  userSelect: 'none',
  display: 'block',
});
